import type { AppDescription } from "../common/components/SerializedApp";
import { getTableProperty, getColumnProperty } from "../generator/components/property-utils";
import type { TableGlideType, TypeSchema, UserProfileDescription } from "../common/description";
import { findTable, GlobalVariableNames } from "../common/description";
import type { ForceValueProducer, UserProfileTableInfo, ValueProducer } from "../common/components/types";
import { asString, isLoadedDefined, loadedDefinedMap } from "../common/components/component-helpers";
import { isPrimitiveValue } from "../common/components/computation-types";
import { definedMap, mapFilterUndefined } from "@glideapps/ts-necessities";
import { isArray, normalizeEmailAddress } from "../support";

export function getUserProfileTableInfo({
    userProfile,
}: {
    readonly userProfile?: UserProfileDescription;
}): UserProfileTableInfo | undefined {
    if (userProfile === undefined) return undefined;

    const tableName = getTableProperty(userProfile.userProfileTable);
    const emailColumnName = getColumnProperty(userProfile.emailColumn);
    const nameColumnName = getColumnProperty(userProfile.nameColumn);
    const imageColumnName = getColumnProperty(userProfile.imageColumn);
    const rolesColumnName = getColumnProperty(userProfile.rolesColumn);

    if (
        tableName === undefined ||
        emailColumnName === undefined ||
        nameColumnName === undefined ||
        imageColumnName === undefined
    ) {
        return undefined;
    }

    return { tableName, emailColumnName, nameColumnName, imageColumnName, rolesColumnName };
}

export function getUserProfileTableForAppAndSchema(
    appDescription: AppDescription | undefined,
    schema: TypeSchema | undefined
): TableGlideType | undefined {
    if (schema === undefined) return undefined;
    if (appDescription === undefined) return undefined;
    const userProfileTableName = getUserProfileTableInfo(appDescription)?.tableName;
    if (userProfileTableName === undefined) return undefined;
    return findTable(schema, userProfileTableName);
}

export interface UserProfileRow {
    readonly email: string;
    readonly name: string | undefined;
    readonly imageURL: string | undefined;
    readonly roles: readonly string[];
    readonly key: string;
}

export function forceUserProfileRow(
    rowVP: ValueProducer,
    key: string,
    info: UserProfileTableInfo,
    force: ForceValueProducer
): UserProfileRow | undefined {
    const inspector = rowVP.getObjectInspector(force);
    if (!isLoadedDefined(inspector)) return undefined;

    const email = loadedDefinedMap(definedMap(inspector.getProperty(info.emailColumnName), force), v =>
        normalizeEmailAddress(asString(v))
    );
    if (email === undefined) return undefined;

    const name = loadedDefinedMap(definedMap(inspector.getProperty(info.nameColumnName), force), asString);
    const imageURL = loadedDefinedMap(definedMap(inspector.getProperty(info.imageColumnName), force), asString);

    let roles: string[] | undefined;
    if (info.rolesColumnName !== undefined) {
        const rolesValue = definedMap(inspector.getProperty(info.rolesColumnName), force);
        if (isLoadedDefined(rolesValue)) {
            if (isPrimitiveValue(rolesValue)) {
                roles = [asString(rolesValue)];
            } else if (isArray(rolesValue)) {
                roles = mapFilterUndefined(rolesValue, vp => loadedDefinedMap(force(vp), asString));
            }
        }
    }

    roles = mapFilterUndefined(roles ?? [], r => {
        r = r.trim().toLowerCase();
        if (r === "") return undefined;
        return r;
    });

    return { key, email, name, imageURL, roles };
}

export function makeUserProfileGlobalVariables(
    userProfile: UserProfileTableInfo | undefined
): ReadonlyMap<GlobalVariableNames, string | undefined> {
    const map = new Map<GlobalVariableNames, string | undefined>();
    map.set(
        GlobalVariableNames.UserProfileTableName,
        definedMap(userProfile, p => p.tableName.name)
    );
    map.set(
        GlobalVariableNames.UserProfileEmailColumnName,
        definedMap(userProfile, p => p.emailColumnName)
    );
    map.set(
        GlobalVariableNames.UserProfileNameColumnName,
        definedMap(userProfile, p => p.nameColumnName)
    );
    map.set(
        GlobalVariableNames.UserProfileImageColumnName,
        definedMap(userProfile, p => p.imageColumnName)
    );

    return map;
}
